import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { getApiUrl } from '../../config/api';
import ImageUpload from '../../components/ImageUpload';
import { MAX_PRODUCT_IMAGES } from '../../utils/cataloguePdf';

const sizeOptions = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL', '5XL', '6XL', '7XL'];

const initialItem = {
  title: '',
  description: '',
  retailPrice: '',
  wholesalePrice: '',
  sizes: [],
  colors: [],
  images: [],
  isActive: true
};

const toImageUrl = (img) => {
  if (!img) return '';
  if (typeof img === 'string') return img;
  return img.url || '';
};

const formatPrice = (value) => {
  if (value == null || value === '' || Number(value) === 0) return '';
  return `₹${Number(value).toLocaleString('en-IN')}`;
};

// Admins need to spot a missing price, so an unset value stays visible as a dash
// instead of collapsing away like it does on the customer catalogue.
const PriceValue = ({ value }) => {
  const amount = formatPrice(value);
  if (!amount) return <span className="text-gray-400" title="Not set">—</span>;
  return <span className="text-gray-900">{amount}</span>;
};

const Catalogue = () => {
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [item, setItem] = useState(initialItem);
  const [search, setSearch] = useState('');
  const [colorInput, setColorInput] = useState('');
  const [imageUrls, setImageUrls] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchItems = () => {
    fetch(getApiUrl('/api/admin/catalogues'), { headers: { 'x-admin': 'true' } })
      .then(res => res.json())
      .then(data => setItems(Array.isArray(data) ? data : []));
  };

  useEffect(() => {
    fetchItems();
  }, []);

  useEffect(() => {
    document.body.style.overflow = showModal ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showModal]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setItem(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSizeChange = (e) => {
    const { value, checked } = e.target;
    setItem(prev => ({
      ...prev,
      sizes: checked
        ? [...(prev.sizes || []), value]
        : (prev.sizes || []).filter(size => size !== value)
    }));
  };

  const handleEdit = (catalogueItem) => {
    setEditingItem(catalogueItem);
    setItem({
      title: catalogueItem.title || '',
      description: catalogueItem.description || '',
      retailPrice: catalogueItem.retailPrice ?? catalogueItem.price ?? '',
      wholesalePrice: catalogueItem.wholesalePrice ?? '',
      sizes: catalogueItem.sizes || [],
      colors: catalogueItem.colors || [],
      images: (catalogueItem.images || []).map(toImageUrl).filter(Boolean),
      isActive: catalogueItem.isActive !== false
    });
    setColorInput(Array.isArray(catalogueItem.colors) ? catalogueItem.colors.join(', ') : '');
    setImageUrls((catalogueItem.images || []).map(toImageUrl).filter(Boolean));
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...item,
      retailPrice: Number(item.retailPrice) || 0,
      wholesalePrice: Number(item.wholesalePrice) || 0,
      colors: colorInput.split(',').map(c => c.trim()).filter(Boolean),
      images: (imageUrls || []).map(toImageUrl).filter(Boolean)
    };
    try {
      const url = editingItem
        ? getApiUrl(`/api/admin/catalogues/${editingItem._id}`)
        : getApiUrl('/api/admin/catalogues');

      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'x-admin': 'true',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Failed to ${editingItem ? 'update' : 'add'} catalogue product`);
      }

      const saved = await response.json();
      if (editingItem) {
        setItems(prev => prev.map(i => i._id === editingItem._id ? saved : i));
      } else {
        setItems(prev => [...prev, saved]);
      }

      closeModal();
    } catch (error) {
      console.error('Error saving catalogue item:', error);
      alert(`Failed to ${editingItem ? 'update' : 'add'} product. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this product from the catalogue?')) return;
    try {
      const response = await fetch(getApiUrl(`/api/admin/catalogues/${id}`), {
        method: 'DELETE',
        headers: { 'x-admin': 'true' }
      });
      if (!response.ok) throw new Error('Failed to delete');
      setItems(prev => prev.filter(i => i._id !== id));
    } catch (error) {
      console.error('Error deleting catalogue item:', error);
      alert('Failed to delete. Please try again.');
    }
  };

  const openModal = () => {
    setEditingItem(null);
    setItem(initialItem);
    setColorInput('');
    setImageUrls([]);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setItem(initialItem);
    setColorInput('');
    setImageUrls([]);
  };

  const filtered = items.filter(i =>
    `${i.title} ${i.description}`.toLowerCase().includes(search.toLowerCase())
  );

  const retailOf = (row) => row.retailPrice ?? row.price;

  return (
    <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-5">
        <h1 className="text-xl sm:text-2xl font-bold">Catalogue</h1>
        <button
          onClick={openModal}
          className="bg-pink-600 text-white px-4 py-2.5 rounded-lg flex items-center justify-center hover:bg-pink-700 w-full sm:w-auto"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Add Product to Catalogue
        </button>
      </div>

      <div className="mb-5">
        <div className="relative">
          <input
            type="text"
            placeholder="Search catalogue products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border rounded-lg"
          />
          <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-3" />
        </div>
      </div>

      {/* Mobile / tablet cards */}
      <div className="space-y-3 lg:hidden">
        {filtered.map((row) => (
          <div key={row._id} className="bg-white rounded-lg shadow p-4">
            <div className="flex gap-3">
              {toImageUrl(row.images?.[0]) ? (
                <img src={toImageUrl(row.images[0])} alt={row.title} className="h-20 w-20 flex-shrink-0 object-cover rounded" />
              ) : (
                <div className="h-20 w-20 flex-shrink-0 bg-gray-200 rounded" />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold text-gray-900 break-words">{row.title}</p>
                  <span className={`flex-shrink-0 px-2 py-0.5 text-[11px] font-semibold rounded-full ${row.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {row.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-sm text-gray-500 line-clamp-2 mt-0.5">{row.description}</p>
                <div className="mt-2 grid grid-cols-2 gap-2 max-w-xs">
                  <div>
                    <p className="uppercase tracking-wider text-[10px] text-gray-400">Retail</p>
                    <p className="text-sm font-semibold"><PriceValue value={retailOf(row)} /></p>
                  </div>
                  <div>
                    <p className="uppercase tracking-wider text-[10px] text-gray-400">Wholesale</p>
                    <p className="text-sm font-semibold"><PriceValue value={row.wholesalePrice} /></p>
                    <p className="text-[10px] text-gray-400">{(row.sizes || []).length || 0} pcs / set</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t text-sm text-gray-600 space-y-1">
              <p><span className="text-gray-400">Sizes:</span> {(row.sizes || []).join(', ') || '—'}</p>
              <p><span className="text-gray-400">Colors:</span> {(row.colors || []).join(', ') || '—'}</p>
            </div>
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => handleEdit(row)}
                className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-pink-200 text-pink-600 hover:bg-pink-50"
              >
                <PencilIcon className="w-4 h-4" /> Edit
              </button>
              <button
                onClick={() => handleDelete(row._id)}
                className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
              >
                <TrashIcon className="w-4 h-4" /> Delete
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
            No catalogue products yet. Add one to show it in the user catalogue.
          </div>
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden lg:block bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Retail</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Wholesale</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Set size</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sizes</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Colors</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filtered.map((row) => (
              <tr key={row._id}>
                <td className="px-4 py-4 whitespace-nowrap">
                  {toImageUrl(row.images?.[0]) ? (
                    <img src={toImageUrl(row.images[0])} alt={row.title} className="h-12 w-12 object-cover rounded" />
                  ) : (
                    <div className="h-12 w-12 bg-gray-200 rounded" />
                  )}
                </td>
                <td className="px-4 py-4 max-w-xs">
                  <div className="font-medium text-gray-900">{row.title}</div>
                  <div className="text-sm text-gray-500 line-clamp-1">{row.description}</div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium"><PriceValue value={retailOf(row)} /></td>
                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium"><PriceValue value={row.wholesalePrice} /></td>
                <td className="px-4 py-4 whitespace-nowrap text-sm">{(row.sizes || []).length || 0} pcs</td>
                <td className="px-4 py-4 text-sm">{(row.sizes || []).join(', ') || '—'}</td>
                <td className="px-4 py-4 text-sm">{(row.colors || []).join(', ') || '—'}</td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${row.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {row.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-right sticky right-0 bg-white">
                  <button className="text-pink-600 hover:text-pink-800 mr-3" onClick={() => handleEdit(row)} aria-label="Edit">
                    <PencilIcon className="w-5 h-5" />
                  </button>
                  <button className="text-red-500 hover:text-red-700" onClick={() => handleDelete(row._id)} aria-label="Delete">
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="px-6 py-8 text-center text-gray-500">
                  No catalogue products yet. Add one to show it in the user catalogue.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-end sm:items-center justify-center sm:p-4">
          <div className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-lg flex flex-col max-h-[92vh] sm:max-h-[90vh]">
            <div className="flex items-center justify-between px-4 sm:px-8 py-4 border-b">
              <h2 className="text-lg sm:text-2xl font-bold">
                {editingItem ? 'Edit Catalogue Product' : 'Add Product to Catalogue'}
              </h2>
              <button type="button" onClick={closeModal} className="p-2 -mr-2 text-gray-400 hover:text-gray-600" aria-label="Close">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
              <div className="flex-1 overflow-y-auto overscroll-contain px-4 sm:px-8 py-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Images</label>
                  <p className="text-xs text-gray-500 mb-1">
                    Upload up to {MAX_PRODUCT_IMAGES}. The first is the main photo; the rest appear in a row beneath it in the catalogue and PDF.
                  </p>
                  <ImageUpload
                    onImagesChange={setImageUrls}
                    multiple={true}
                    maxFiles={MAX_PRODUCT_IMAGES}
                    accept="image/*"
                    className="mt-1"
                    initialImages={imageUrls}
                  />
                  {imageUrls.length > 0 && (
                    <p className="mt-1 text-xs font-medium text-[#801c33]">{imageUrls.length} image{imageUrls.length > 1 ? 's' : ''} selected</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Title</label>
                  <input
                    type="text"
                    name="title"
                    value={item.title}
                    onChange={handleChange}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    name="description"
                    value={item.description}
                    onChange={handleChange}
                    required
                    rows={3}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Retail Price (₹)</label>
                    <input
                      type="number"
                      name="retailPrice"
                      value={item.retailPrice}
                      onChange={handleChange}
                      required
                      min="0"
                      step="1"
                      inputMode="numeric"
                      placeholder="e.g. 2499"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">Shown to every customer.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Wholesale Price (₹)</label>
                    <input
                      type="number"
                      name="wholesalePrice"
                      value={item.wholesalePrice}
                      onChange={handleChange}
                      min="0"
                      step="1"
                      inputMode="numeric"
                      placeholder="e.g. 1799"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Price of one full set. Leave empty to hide it in the catalogue.
                    </p>
                    <p className="mt-1 text-xs font-medium text-[#801c33]">
                      {(item.sizes || []).length
                        ? `1 set = ${(item.sizes || []).length} pcs (${(item.sizes || []).join(', ')})`
                        : 'Select sizes below to set how many pieces make one set.'}
                    </p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sizes <span className="font-normal text-gray-500">— the number selected is the set size</span>
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {sizeOptions.map(size => (
                      <label key={size} className="inline-flex items-center px-3 py-2 border rounded-lg">
                        <input
                          type="checkbox"
                          value={size}
                          checked={item.sizes?.includes(size)}
                          onChange={handleSizeChange}
                          className="rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">{size}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Colors (comma separated)</label>
                  <input
                    type="text"
                    value={colorInput}
                    onChange={(e) => setColorInput(e.target.value)}
                    placeholder="e.g., Red, Blue, Gold"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={item.isActive}
                    onChange={handleChange}
                    className="h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">Visible in catalogue</label>
                </div>
              </div>

              <div className="border-t px-4 sm:px-8 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 bg-white rounded-b-lg">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2.5 border rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2.5 bg-pink-600 text-white rounded-md hover:bg-pink-700 disabled:opacity-60"
                >
                  {saving ? 'Saving…' : editingItem ? 'Update' : 'Add to Catalogue'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Catalogue;
